import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSettings } from '../../context/SettingsContext';
import aflPlayerData from '../../assets/afl_player_data_2026.json';
import PlayerVoteProgressionLineGraph from './PlayerVoteProgressionLineGraph';
import VoteProgressionLeaderboard from './VoteProgressionLeaderboard';

const ROUNDS = Array.from({ length: 25 }, (_, i) => i); // 0..24
const LAST_ROUND = 24;
const BASE_RUN_MS = 6000; // full-season duration at 1× speed

const normalize = (value) =>
  (value || '')
    .toString()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Same extraction used by the vote summary table: per-player, per-round vote
// totals for one dataset, guarded by the player→team map.
const buildPerPlayerRoundTotalsFrom = (dataSet, teamMap) => {
  const result = new Map();
  const data = dataSet && dataSet.game_vote_data ? dataSet.game_vote_data : [];
  for (const roundData of data) {
    const round = String(roundData.round);
    for (const game of roundData.game_votes) {
      for (const vote of game.votes) {
        const key = vote.player.toLowerCase();
        if (!teamMap || !teamMap.has(key)) continue;
        const expectedTeam = normalize(teamMap.get(key));
        if (normalize(vote.team) !== expectedTeam) continue;
        if (!result.has(key)) result.set(key, {});
        const r = result.get(key);
        r[round] = (r[round] || 0) + vote.votes;
      }
    }
  }
  return result;
};

const PLAYER_NAME_TO_IMG = (() => {
  const map = new Map();
  for (const ap of aflPlayerData) {
    map.set(ap.player_name, ap.image_src);
  }
  return map;
})();

const DATASET_FIELDS = {
  count1: 'game_vote_data_count_1',
  count2: 'game_vote_data_count_2',
  count3: 'game_vote_data_count_3',
  count4: 'game_vote_data_count_4',
  count5: 'game_vote_data_count_5',
  betfair: 'game_vote_data_betfair',
  espn: 'game_vote_data_espn',
  byb: 'game_vote_data_byb',
};

const TOP_OPTIONS = [3, 5, 10, 20];
const SPEED_OPTIONS = [0.25, 0.5, 1];
// Desktop defaults to the slow cinematic run; mobile defaults to 1× where the
// shorter run suits the smaller chart.
const DEFAULT_SPEED =
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches ? 1 : 0.25;

const DATASET_KEY_TO_LABEL = {
  count1: 'BG 1',
  count2: 'BG 2',
  count3: 'BG 3',
  count4: 'BG 4',
  count5: 'BG 5',
  betfair: 'Betfair',
  espn: 'ESPN',
  byb: 'BYB',
};

const selectArrowStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 8px center',
  backgroundSize: '14px',
};

const selectClass =
  'bg-slate-900/50 border border-slate-700/60 text-xs font-mono text-white rounded-lg px-3 h-9 focus:outline-none focus:border-emerald-500/60 cursor-pointer appearance-none pr-8 transition-colors';

const controlIconButtonClass =
  'w-6 h-6 flex items-center justify-center rounded text-slate-300 hover:text-white hover:bg-slate-700/60 transition-colors';

const LeaderboardVoteProgression = ({ voteDataRemote = null }) => {
  const { getEnabledKeys, selectedDataset, setSelectedDataset } = useSettings();
  const enabledKeys = getEnabledKeys();
  const [topCount, setTopCount] = useState(20);
  const [hovered, setHovered] = useState(null);

  // Same dataset guard as the vote summary: fall back to All when the
  // selected dataset gets disabled.
  useEffect(() => {
    if (selectedDataset !== 'all' && !enabledKeys.includes(selectedDataset)) {
      setSelectedDataset('all');
    }
  }, [enabledKeys, selectedDataset, setSelectedDataset]);

  // ---- Playback engine (frame-based so pause/speed changes apply live) ----
  const [progress, setProgress] = useState(LAST_ROUND);
  const [runActive, setRunActive] = useState(false);
  const [paused, setPaused] = useState(false);
  const [speed, setSpeed] = useState(DEFAULT_SPEED);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const progressRef = useRef(LAST_ROUND);
  const pausedRef = useRef(false);
  const speedRef = useRef(DEFAULT_SPEED);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);
  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  const stopLoop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const tick = useCallback(
    (now) => {
      const dt = Math.min(100, now - lastTsRef.current);
      lastTsRef.current = now;
      if (!pausedRef.current) {
        progressRef.current = Math.min(
          LAST_ROUND,
          progressRef.current + (dt / BASE_RUN_MS) * LAST_ROUND * speedRef.current
        );
        setProgress(progressRef.current);
      }
      if (progressRef.current >= LAST_ROUND) {
        setRunActive(false);
        setPaused(false);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    },
    []
  );

  // Begin playing from the current progress position.
  const startLoop = useCallback(() => {
    stopLoop();
    setRunActive(true);
    setPaused(false);
    lastTsRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  }, [stopLoop, tick]);

  const startRun = useCallback(() => {
    progressRef.current = 0;
    setProgress(0);
    startLoop();
  }, [startLoop]);

  // Play/pause button: toggles pause mid-run; when idle, resumes from the
  // scrubbed position (or restarts from round 0 if already at the end).
  const playToggle = useCallback(() => {
    if (runActive) {
      setPaused((p) => !p);
      return;
    }
    if (progressRef.current >= LAST_ROUND) {
      progressRef.current = 0;
      setProgress(0);
    }
    startLoop();
  }, [runActive, startLoop]);

  const stopRun = useCallback(() => {
    stopLoop();
    progressRef.current = LAST_ROUND;
    setProgress(LAST_ROUND);
    setRunActive(false);
    setPaused(false);
  }, [stopLoop]);

  const seekTo = useCallback((round) => {
    progressRef.current = Math.max(0, Math.min(LAST_ROUND, round));
    setProgress(progressRef.current);
  }, []);

  useEffect(() => stopLoop, [stopLoop]);

  // ---- Series building (enabled datasets only) ----
  const players = voteDataRemote?.leaderboard || [];

  const teamMap = useMemo(() => {
    const map = new Map();
    for (const p of players) {
      map.set(p.name.toLowerCase(), (p.full_team_name || p.team || '').toString());
    }
    return map;
  }, [players]);

  // Per-round totals for the selected dataset ("all" averages every enabled
  // dataset, mirroring the vote summary behaviour).
  const sourceKeys = selectedDataset === 'all' ? enabledKeys : [selectedDataset];
  const enabledTotalMaps = useMemo(() => {
    const wrap = (d) => (Array.isArray(d) ? { game_vote_data: d } : d);
    if (!voteDataRemote || teamMap.size === 0 || !sourceKeys.length) return [];
    return sourceKeys
      .map((k) => DATASET_FIELDS[k])
      .filter(Boolean)
      .map((field) => buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote[field]), teamMap));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voteDataRemote, teamMap, sourceKeys.join(',')]);

  // Top 20 players by total projected votes (averaged across enabled
  // datasets), each with a cumulative round-by-round series.
  const series = useMemo(() => {
    if (!players.length || !enabledTotalMaps.length) return [];
    const rows = players.map((p) => {
      const lower = p.name.toLowerCase();
      const cumulative = [];
      let running = 0;
      for (const r of ROUNDS) {
        let sum = 0;
        for (const m of enabledTotalMaps) {
          const rec = m.get(lower) || {};
          const v = Number(rec[String(r)] || 0);
          sum += isNaN(v) ? 0 : v;
        }
        running += sum / enabledTotalMaps.length;
        cumulative.push(running);
      }
      return {
        name: p.name,
        team: p.team,
        originalPosition: p.position,
        image: PLAYER_NAME_TO_IMG.get(p.name) || null,
        values: cumulative,
        total: running,
      };
    });
    rows.sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.originalPosition - b.originalPosition;
    });
    return rows.slice(0, 20);
  }, [players, enabledTotalMaps]);

  const visibleSeries = useMemo(() => series.slice(0, topCount), [series, topCount]);

  // Changing the player set (Top N / datasets) cancels any active run.
  const seriesKey = visibleSeries.map((s) => s.name).join('|');
  useEffect(() => {
    stopLoop();
    progressRef.current = LAST_ROUND;
    setProgress(LAST_ROUND);
    setRunActive(false);
    setPaused(false);
    setHovered(null);
  }, [seriesKey, stopLoop]);

  const displayRound = Math.min(LAST_ROUND, Math.floor(progress));

  return (
    <div className="bg-[#0a1120] rounded-2xl border border-slate-800 overflow-hidden flex flex-col md:h-[calc(100vh-3rem)]">
      {/* Header */}
      <div className="bg-slate-800/20 border-b border-slate-800/80 px-3 md:px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 flex-shrink-0">
        <div>
          <h2 className="text-sm font-semibold text-white tracking-tight">Vote Progression</h2>
          <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 mt-0.5">
            Cumulative projected votes &middot; Rounds 0&ndash;24
          </p>
        </div>

        {/* Mobile: row 1 is Dataset | Top N | Run (Run jumps ahead via order),
            row 2 is the full-width playback pill. Desktop keeps the single
            row: dataset, top N, playback, run. */}
        <div className="w-full md:w-auto flex flex-wrap items-center gap-2">
          <select
            value={selectedDataset}
            onChange={(e) => setSelectedDataset(e.target.value)}
            className={`${selectClass} flex-1 md:flex-none min-w-0`}
            style={selectArrowStyle}
            title="Choose dataset for the progression"
          >
            <option value="all">Dataset: All</option>
            {enabledKeys.map((k) => (
              <option key={k} value={k}>
                Dataset: {DATASET_KEY_TO_LABEL[k] || k}
              </option>
            ))}
          </select>

          <select
            value={topCount}
            onChange={(e) => setTopCount(Number(e.target.value))}
            className={`${selectClass} flex-1 md:flex-none min-w-0`}
            style={selectArrowStyle}
            title="How many leaders to plot"
          >
            {TOP_OPTIONS.map((n) => (
              <option key={n} value={n}>
                Top {n}
              </option>
            ))}
          </select>

          {/* Playback controls — always visible; the scrubber also works while
              idle so any round can be inspected without running */}
          <div className="order-last md:order-none w-full md:w-auto flex items-center gap-2 bg-slate-900/50 border border-slate-700/60 rounded-lg px-2.5 h-9">
            <button
              onClick={playToggle}
              disabled={!visibleSeries.length}
              className={`${controlIconButtonClass} disabled:opacity-40 disabled:cursor-not-allowed`}
              title={runActive && !paused ? 'Pause' : 'Play'}
              aria-label={runActive && !paused ? 'Pause progression' : 'Play progression'}
            >
              {runActive && !paused ? (
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" />
                </svg>
              ) : (
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M8 5.14v13.72c0 .86.94 1.39 1.68.94l11.02-6.86a1.1 1.1 0 000-1.88L9.68 4.2A1.1 1.1 0 008 5.14z" />
                </svg>
              )}
            </button>
            <button
              onClick={stopRun}
              disabled={!runActive && progress >= LAST_ROUND}
              className={`${controlIconButtonClass} disabled:opacity-40 disabled:cursor-not-allowed`}
              title="Stop and show final standings"
              aria-label="Stop progression"
            >
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <rect x="6" y="6" width="12" height="12" rx="1.5" />
              </svg>
            </button>
            <div className="w-px h-4 bg-slate-700/60" />
            <span className="text-[10px] font-mono font-semibold tabular-nums text-emerald-300 w-10 text-center">
              RD {displayRound}
            </span>
            <input
              type="range"
              min={0}
              max={LAST_ROUND}
              step={1}
              value={displayRound}
              onChange={(e) => seekTo(Number(e.target.value))}
              disabled={!visibleSeries.length}
              className="vpg-scrub flex-1 md:flex-none min-w-0 w-24 md:w-32 h-1 bg-slate-800 rounded-full appearance-none cursor-pointer focus:outline-none disabled:opacity-40"
              title="Jump to a specific round"
              aria-label="Jump to round"
            />
            <div className="w-px h-4 bg-slate-700/60" />
            <select
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              className="bg-transparent text-[10px] font-mono font-semibold text-slate-300 focus:outline-none cursor-pointer"
              title="Playback speed"
              aria-label="Playback speed"
            >
              {SPEED_OPTIONS.map((s) => (
                <option key={s} value={s} className="bg-slate-900">
                  {s.toFixed(2)}&times;
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={startRun}
            disabled={!visibleSeries.length}
            className="flex-1 md:flex-none h-9 px-3 text-xs font-mono font-semibold rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 hover:border-emerald-500/50 transition-all flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Animate the vote progression round by round"
          >
            <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5.14v13.72c0 .86.94 1.39 1.68.94l11.02-6.86a1.1 1.1 0 000-1.88L9.68 4.2A1.1 1.1 0 008 5.14z" />
            </svg>
            {/* Short label on mobile so the button fits the shared top row */}
            <span className="md:hidden whitespace-nowrap">{runActive ? 'Restart' : 'Run'}</span>
            <span className="hidden md:inline whitespace-nowrap">{runActive ? 'Restart' : 'Run Progression'}</span>
          </button>
        </div>
      </div>

      <style>{`
        .vpg-scrub::-webkit-slider-thumb {
          appearance: none;
          height: 12px;
          width: 12px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #0f172a;
          box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.4);
        }
        .vpg-scrub::-moz-range-thumb {
          height: 12px;
          width: 12px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #0f172a;
          box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.4);
        }
      `}</style>

      {/* Body: chart (70%) + live leaderboard (30%) */}
      <div className="p-3 md:p-4 flex-1 min-h-0">
        {visibleSeries.length ? (
          <div className="h-full grid grid-cols-1 md:grid-cols-10 gap-3">
            <div className="md:col-span-7 min-h-0 flex flex-col">
              <PlayerVoteProgressionLineGraph
                series={visibleSeries}
                progress={progress}
                runActive={runActive}
                paused={paused}
                hovered={hovered}
                onHover={setHovered}
              />
            </div>
            {/* Mobile: natural height so every player row is visible without
                scrolling; desktop keeps the fixed column with internal scroll. */}
            <div className="md:col-span-3 md:min-h-0 md:h-auto">
              <VoteProgressionLeaderboard
                series={visibleSeries}
                progress={progress}
                runActive={runActive}
                hovered={hovered}
                onHover={setHovered}
              />
            </div>
          </div>
        ) : (
          <div className="h-full min-h-[16rem] flex items-center justify-center rounded-lg border border-dashed border-slate-800/80 bg-[#0a1120]/40">
            <div className="text-center">
              <p className="text-slate-500 text-xs font-mono">No progression data to display</p>
              <p className="text-[10px] mt-1 text-slate-600 font-mono uppercase tracking-[0.12em]">
                {enabledKeys.length ? 'Vote data not loaded' : 'Enable at least one dataset'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardVoteProgression;
